import { useMemo, useRef, useState } from "react";
import { Check, Calculator, Info, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { trackEvent, EVENTS } from "@/utils/track";

const BENEFITS = [
  "Menos no-shows.",
  "Menos cancelaciones sin seguimiento.",
  "Más horas de sillón ocupadas.",
  "Menos llamadas manuales.",
  "Menos carga para recepción.",
  "Más tratamientos iniciados.",
  "Mayor previsibilidad de la agenda.",
  "Mejor experiencia para el paciente.",
];

const eur = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

export default function BenefitsCalculator() {
  const [citasMes, setCitasMes] = useState(300);
  const [noshowPct, setNoshowPct] = useState(10);
  const [precioMedio, setPrecioMedio] = useState(80);
  const [gabinetes, setGabinetes] = useState(3);
  const [recuperablePct, setRecuperablePct] = useState(40);
  const tracked = useRef(false);

  const onUse = () => {
    if (!tracked.current) {
      tracked.current = true;
      trackEvent(EVENTS.CALCULATOR_USE);
    }
  };

  const results = useMemo(() => {
    const citasPerdidas = Math.round((citasMes * noshowPct) / 100);
    const perdidaMensual = citasPerdidas * precioMedio;
    const perdidaAnual = perdidaMensual * 12;
    const recuperable = Math.round((perdidaMensual * recuperablePct) / 100);
    return { citasPerdidas, perdidaMensual, perdidaAnual, recuperable };
  }, [citasMes, noshowPct, precioMedio, recuperablePct]);

  const NumberField = ({ id, label, value, onChange, min = 0, max, suffix }) => (
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-navy">
        {label}
      </Label>
      <div className="relative mt-1.5">
        <Input
          id={id}
          data-testid={`calc-input-${id}`}
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            onUse();
            onChange(Math.max(min, Number(e.target.value) || 0));
          }}
          className="h-11 rounded-lg border-[#DCE5F0] bg-white pr-10 text-navy focus-visible:ring-navy-light"
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-steel">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );

  const SliderField = ({ id, label, value, onChange, min, max, step = 1 }) => (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-sm font-semibold text-navy">
          {label}
        </Label>
        <span
          data-testid={`calc-value-${id}`}
          className="rounded-md bg-platinum px-2 py-0.5 text-sm font-bold text-navy"
        >
          {value}%
        </span>
      </div>
      <Slider
        id={id}
        data-testid={`calc-slider-${id}`}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([v]) => {
          onUse();
          onChange(v);
        }}
        className="mt-3"
        aria-label={label}
      />
    </div>
  );

  return (
    <section id="resultados" className="bg-platinum py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-navy-light">
              Resultados
            </p>
            <h2
              data-testid="benefits-title"
              className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-[2.6rem] lg:leading-[1.15]"
            >
              Convierte horas vacías en ingresos recuperados.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
              Un sistema de confirmación y rescate automático puede ayudarte a recuperar{" "}
              <strong className="font-semibold text-navy">hasta 2.000 € al mes</strong> en
              citas perdidas, según el tamaño de la clínica, el volumen de citas y el
              nivel actual de no-shows.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-steel">
              * Cifra orientativa: los resultados dependen del volumen de citas, historial
              de cancelaciones, precio medio de los tratamientos y capacidad de la
              clínica. No es una garantía universal.
            </p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm font-medium text-navy">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E7F4EC]">
                    <Check className="h-3 w-3 text-recover" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div
            id="calculadora"
            data-testid="calculator"
            className="scroll-mt-28 rounded-2xl border border-[#E4E9F2] bg-white p-6 shadow-[0_24px_60px_-24px_rgba(11,31,58,0.18)] sm:p-8"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy">
                <Calculator className="h-5 w-5 text-white" strokeWidth={1.8} />
              </div>
              <h3 className="font-heading text-xl font-bold text-navy">
                Calcula el coste mensual de tus no-shows
              </h3>
            </div>

            <div className="mt-7 space-y-6">
              <NumberField
                id="citas-mes"
                label="Número de citas al mes"
                value={citasMes}
                onChange={setCitasMes}
                min={1}
              />
              <SliderField
                id="noshow-pct"
                label="Porcentaje aproximado de no-shows"
                value={noshowPct}
                onChange={setNoshowPct}
                min={0}
                max={40}
              />
              <NumberField
                id="precio-medio"
                label="Precio medio por cita"
                value={precioMedio}
                onChange={setPrecioMedio}
                min={1}
                suffix="€"
              />
              <NumberField
                id="gabinetes"
                label="Número de gabinetes"
                value={gabinetes}
                onChange={setGabinetes}
                min={1}
              />
              <SliderField
                id="recuperable-pct"
                label="Porcentaje estimado de citas recuperables"
                value={recuperablePct}
                onChange={setRecuperablePct}
                min={0}
                max={90}
              />
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-platinum p-4">
                <p data-testid="calc-result-citas-perdidas" className="font-heading text-2xl font-bold text-navy">
                  {results.citasPerdidas}
                </p>
                <p className="mt-1 text-[11px] font-medium leading-tight text-steel">
                  Citas perdidas aprox. al mes
                </p>
              </div>
              <div className="rounded-xl bg-platinum p-4">
                <p data-testid="calc-result-perdida-mensual" className="font-heading text-2xl font-bold text-navy">
                  {eur.format(results.perdidaMensual)}
                </p>
                <p className="mt-1 text-[11px] font-medium leading-tight text-steel">
                  Pérdida mensual estimada
                </p>
              </div>
              <div className="rounded-xl bg-platinum p-4">
                <p data-testid="calc-result-perdida-anual" className="font-heading text-2xl font-bold text-navy">
                  {eur.format(results.perdidaAnual)}
                </p>
                <p className="mt-1 text-[11px] font-medium leading-tight text-steel">
                  Pérdida anual estimada
                </p>
              </div>
              <div className="rounded-xl border border-[#DDEAE2] bg-[#F2F9F5] p-4">
                <p data-testid="calc-result-recuperable" className="font-heading text-2xl font-bold text-recover">
                  {eur.format(results.recuperable)}
                </p>
                <p className="mt-1 text-[11px] font-medium leading-tight text-steel">
                  Ingresos potencialmente recuperables al mes
                </p>
              </div>
            </div>

            <p className="mt-5 flex items-start gap-2 text-[11px] leading-relaxed text-steel">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-navy-light" />
              Esta calculadora ofrece una estimación orientativa. Los resultados reales
              dependen de los datos y procesos de cada clínica.
            </p>

            <Button
              asChild
              data-testid="calc-cta-button"
              className="mt-6 w-full rounded-lg bg-navy py-6 text-base font-semibold text-white transition-all duration-200 hover:bg-navy-light"
              onClick={() => trackEvent(EVENTS.CTA_PRINCIPAL, { ubicacion: "calculadora" })}
            >
              <a href="#auditoria">
                Quiero mi auditoría gratuita
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
